import { Component, computed, OnDestroy, OnInit, signal } from '@angular/core';
import { Subject, throwError } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { NotificationService } from '../../../core/services';

import { BillingService } from '../services/billing.service';
import { UsageResult, UsageEndpointStat, UsageSeriesPoint } from '../models/usage.model';

type DropdownName = 'range' | 'sort';
type EndpointSort = 'requests' | 'cost';
type ChartMetric = 'requests' | 'costCents';

@Component({
  selector: 'app-billing-usage',
  standalone: false,
  templateUrl: './billing-usage.html',
  styleUrl: './billing-usage.scss',
})
export class BillingUsage implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  usage: UsageResult | null = null;
  isLoading = false;
  /** What the line chart plots - toggled by the Requests / Cost segmented control. */
  chartMetric: ChartMetric = 'requests';

  readonly rangeOptions = [
    { days: 7, label: 'Last 7 days' },
    { days: 30, label: 'Last 30 days' },
    { days: 90, label: 'Last 90 days' },
    { days: 365, label: 'Last year' },
  ];
  readonly selectedRangeDays = signal(30);

  endpointSort: EndpointSort = 'requests';

  openDropdown: DropdownName | null = null;

  hoveredIndex: number | null = null;

  private readonly CHART_PAD_PCT = 8;
  private readonly SPARK_W = 120;
  private readonly SPARK_H = 32;
  private readonly SPARK_PAD = 3;

  constructor(
    private billingService: BillingService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.loadUsage(this.computeRange(this.selectedRangeDays()));
  }

  private loadUsage(range: { from: string; to: string }): void {
    this.isLoading = true;
    this.billingService.getUsage(range)
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          this.notificationService.error('Failed to load usage data. Please try again later.');
          return throwError(() => error);
        }),
        tap(usage => { this.usage = usage; }),
        finalize(() => { this.isLoading = false; }),
      ).subscribe();
  }

  private computeRange(days: number): { from: string; to: string } {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - (days - 1));
    return { from: this.isoDate(from), to: this.isoDate(to) };
  }

  private isoDate(d: Date): string {
    return d.toISOString().slice(0, 10);
  }

  toggleDropdown(name: DropdownName, event: MouseEvent): void {
    event.stopPropagation();
    const wasOpen = this.openDropdown === name;
    this.openDropdown = wasOpen ? null : name;
  }

  /** Bound to (appClickOutside) on each dropdown wrapper in the template
   *  scoped to that wrapper's own name, not a blanket close.
   *
   *  This page has two independent dropdowns (range, sort) sharing one
   *  `openDropdown` field, each with its own ClickOutsideDirective
   *  instance. That directive fires on `mousedown`, before `click`. If
   *  this closed unconditionally, clicking an item *inside* the range
   *  dropdown would still register as "outside" the *sort* dropdown's own
   *  wrapper its listener would fire on that same mousedown and null out
   *  openDropdown, tearing the range menu (and the item you're mid-click
   *  on) out of the DOM before the browser ever dispatches the click
   *  event. selectRange() would then silently never run. Only closing
   *  when `name` matches what's actually open avoids that cross-closing. */
  closeDropdownIfOpen(name: DropdownName): void {
    if (this.openDropdown === name) {
      this.openDropdown = null;
    }
  }

  /** View-only: the usage request finished without data, so show the error card. */
  get loadFailed(): boolean {
    return !this.isLoading && !this.usage;
  }

  retry(): void {
    this.loadUsage(this.computeRange(this.selectedRangeDays()));
  }

  selectRange(days: number): void {
    this.selectedRangeDays.set(days);
    this.openDropdown = null;
    this.loadUsage(this.computeRange(days));
  }

  /** "Last 30 days" etc., matching whichever rangeOptions entry is selected
   *  same convention as Home and Admin Overview (see home.ts), showing
   *  the picked option's own label instead of the computed from/to dates.
   *  A computed signal instead of a getter: a getter re-runs on every
   *  change-detection pass (e.g. a mousemove), a computed only re-runs
   *  when selectedRangeDays actually changes. */
  readonly rangeLabel = computed(() => this.rangeOptions.find(opt => opt.days === this.selectedRangeDays())?.label ?? '');

  selectSort(sort: EndpointSort): void {
    this.endpointSort = sort;
  }

  // ── Endpoint breakdown ──────────────────────────────────────────────
  sortedEndpoints(): UsageEndpointStat[] {
    const list = this.usage?.byEndpoint ?? [];
    return [...list].sort((a, b) => this.endpointMetric(b) - this.endpointMetric(a));
  }

  private endpointMetric(e: UsageEndpointStat): number {
    return this.endpointSort === 'requests' ? e.requests : e.costCents;
  }

  /** Bar width for the by-endpoint breakdown, relative to the busiest endpoint by the current sort metric. */
  endpointBarPct(e: UsageEndpointStat): number {
    const list = this.usage?.byEndpoint ?? [];
    const max = Math.max(...list.map(x => this.endpointMetric(x)), 1);
    return Math.round((this.endpointMetric(e) / max) * 100);
  }

  // ── Daily requests line chart ───────────────────────────────────────
  /** Y-axis max, rounded to a clean step (1/2/2.5/5/10 × a power of ten) never a raw data max. */
  get chartMax(): number {
    const max = Math.max(...(this.usage?.series.map(s => s[this.chartMetric]) ?? [0]), 0);
    return this.niceMax(max);
  }

  private niceMax(value: number): number {
    if (value <= 0) return 1;
    const pow = Math.pow(10, Math.floor(Math.log10(value)));
    for (const step of [1, 2, 2.5, 5, 10]) {
      if (value <= step * pow) return step * pow;
    }
    return 10 * pow;
  }

  chartYTicks(): number[] {
    const max = this.chartMax;
    return [max, max / 2, 0];
  }

  chartPointXPct(i: number): number {
    const n = (this.usage?.series.length ?? 1) - 1;
    return n > 0 ? (i / n) * 100 : 0;
  }

  chartPointYPct(value: number): number {
    const usable = 100 - this.CHART_PAD_PCT * 2;
    const frac = this.chartMax ? value / this.chartMax : 0;
    return this.CHART_PAD_PCT + usable * (1 - frac);
  }

  gridYPct(tick: number): number {
    return this.chartPointYPct(tick);
  }

  chartLinePoints(): string {
    return (this.usage?.series ?? [])
      .map((d, i) => `${this.chartPointXPct(i)},${this.chartPointYPct(d[this.chartMetric])}`)
      .join(' ');
  }

  /** Thin the x-axis date labels to at most ~15, evenly, however wide the selected range is. */
  thinnedSeries(): UsageSeriesPoint[] {
    const series = this.usage?.series ?? [];
    const step = Math.max(1, Math.ceil(series.length / 15));
    return series.filter((_, i) => i % step === 0);
  }

  onPointHover(i: number): void {
    this.hoveredIndex = i;
  }

  onChartLeave(): void {
    this.hoveredIndex = null;
  }

  get hoveredPoint(): UsageSeriesPoint | null {
    return this.hoveredIndex != null ? (this.usage?.series[this.hoveredIndex] ?? null) : null;
  }

  setChartMetric(metric: ChartMetric): void {
    this.chartMetric = metric;
    this.hoveredIndex = null;
  }

  /** "day" / "week" / "month" for the chart title - the API's granularity, whatever spelling it uses. */
  get granularityUnit(): string {
    const g = (this.usage?.granularity ?? '').toLowerCase();
    if (g.startsWith('week')) return 'week';
    if (g.startsWith('month')) return 'month';
    return 'day';
  }

  /** Average spend per request over the range, in cents (fractional). Null when there were no requests. */
  get avgCostCents(): number | null {
    const t = this.usage?.totals;
    return t && t.requests ? t.costCents / t.requests : null;
  }

  /** Three decimals, because a typical request costs a fraction of a cent ("€0.005"). */
  formatAvgCost(cents: number, currency: string): string {
    try {
      return (cents / 100).toLocaleString('en-US', {
        style: 'currency', currency: currency.toUpperCase(), minimumFractionDigits: 3, maximumFractionDigits: 3,
      });
    } catch {
      return `${(cents / 100).toFixed(3)} ${currency.toUpperCase()}`;
    }
  }

  // ── Sparklines (stat-tile trend) ────────────────────────────────────
  seriesValues(field: 'requests' | 'costCents' | 'errors'): number[] {
    return (this.usage?.series ?? []).map(s => s[field]);
  }

  sparklinePoints(values: number[]): string {
    if (!values.length) return '';
    const max = Math.max(...values, 0);
    const min = Math.min(...values, 0);
    const range = max - min || 1;
    const step = values.length > 1 ? (this.SPARK_W - this.SPARK_PAD * 2) / (values.length - 1) : 0;
    return values
      .map((v, i) => {
        const x = this.SPARK_PAD + step * i;
        const y = this.SPARK_PAD + (this.SPARK_H - this.SPARK_PAD * 2) * (1 - (v - min) / range);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }

  sparklineEndPoint(values: number[]): { x: number; y: number } | null {
    if (!values.length) return null;
    const points = this.sparklinePoints(values).split(' ');
    const [x, y] = points[points.length - 1].split(',').map(Number);
    return { x, y };
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
